import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useForm} from 'react-hook-form';
import {useAsync} from "react-async-hook";
import ReactModal from "react-modal";
import {userApi} from "../../../../apis/admin/user/userApi";
import {useSystemMessage} from "../../../../hooks/useSystemMessage";
import {useAuth} from "../../../../hooks/useAuth";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import useLoading from "../../../../hooks/useLoading";
import {logError} from "../../../../libs/utils/logError";

interface ProfileEditModalProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSaved: () => Promise<void>;
}

export interface ProfileEditFormValues {
  name: string,
  companyName: string,
  department: string,
  employeeNumber: string,
}

export const ProfileEditModal = (props: ProfileEditModalProps) => {
  const { setShowModal, onSaved} = props;
  const { success } = useSystemMessage();
  const [ abortController, setAbortController ] = useState<AbortController | undefined>();
  const [ isInitialized, setIsInitialized ] = useState<boolean>(true);
  const { state: { currentUser }, reloadCurrentUser } = useAuth();
  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const withLoading = useLoading();
  const location = useLocation();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileEditFormValues>({
    reValidateMode: 'onSubmit',
  });

  useAsync(async () => {
    try {
      withLoading(async () => {
        await reloadCurrentUser();
        setValue('name', currentUser!.name);
        setValue('companyName', currentUser!.companyName);
        setValue('department', currentUser!.extraSalesman!.department);
        setValue('employeeNumber', currentUser!.extraSalesman!.employeeNumber);
        setIsInitialized(true);
      })
    } catch {
      goto('internalServerError');
    }
  }, []);

  const handleSave = async (): Promise<void> => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: ProfileEditFormValues) => {
        try {
          setAbortController(new AbortController());
          await userApi.update(
            {
              name: data.name,
              companyName: data.companyName,
              extraCustomer: null,
              extraSalesman: {
                department: data.department,
                employeeNumber: data.employeeNumber,
              },
            },
            {abortSignal: abortController?.signal});
          setAbortController(undefined);
          await reloadCurrentUser();
          await success('プロフィールを変更しました。');
          await onSaved();

        } catch (e) {
          await logError(
            location.pathname,
            'プロフィールの変更に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="form-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">プロフィール変更</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <form>
            <div className="form-modal-content">
              {isInitialized && (
                <div className="w-full">
                  <div className="mb-4 text-danger text-xs">
                    { apiClientError?.message }
                    { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                  </div>

                  { /* お名前 */ }
                  <div className="mb-4">
                    <label htmlFor="name">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>お名前</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register("name",
                          makeRegisterOptions('お名前', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="name"
                        className="text-field"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.name?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'name',
                        }}
                      />
                    </label>
                  </div>


                  {/* 会社名 */}
                  <div className="mb-4">
                    <label htmlFor="companyName">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>会社名</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register("companyName",
                          makeRegisterOptions('会社名', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="companyName"
                        className="text-field placeholder-opacity-40"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.companyName?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'companyName',
                        }}
                      />
                    </label>
                  </div>

                  {/* 部署 */}
                  <div className="mb-4">
                    <label htmlFor="department">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>部署</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register("department",
                          makeRegisterOptions('部署', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="department"
                        className="text-field"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.department?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'department',
                        }}
                      />
                    </label>
                  </div>

                  {/* 社員番号 */}
                  <div className="mb-4">
                    <label htmlFor="employeeNumber">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>社員番号</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register("employeeNumber",
                          makeRegisterOptions('部署', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="employeeNumber"
                        className="text-field"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.employeeNumber?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'employeeNumber',
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
              <div className="form-modal-footer-container">
                <div className="form-modal-footer">
                  <button
                    className="btn-sub"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    キャンセル
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleSave}
                  >
                    保 存
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};